import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Collapse, message, Modal, Pagination, Row, Select, Space, Table, Tag, Upload } from "antd";
import { CaretRightOutlined, DownloadOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";

import PageHeader from "../Layout/PageHeader";
import DragUpload from "../Form/DragUpload";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";
import AccountReconciliationFilters from "./AccountReconciliationFilters";

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const OUTCOME_COLORS = {
  Matched: "green",
  Mismatched: "red",
  "Credentials changed after booking": "orange",
  "Tracking number not found": "default",
  "Belongs to a different courier": "purple",
  "No account on record": "gold",
  "Duplicate row in upload": "default",
};

const saveBlob = (data, fileName) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const UPLOAD_PARAM = "upload";

const UPLOAD_POLL_INTERVAL_MS = 5000;

const UPLOAD_POLL_LIMIT_MS = 5 * 60 * 1000;

const UPLOAD_ID_PATTERN = /^[A-Za-z0-9]{10}$/;

const UPLOAD_MAX_MB = 10;

const EMPTY_TEXT = {
  processing: "Checking the upload...",
  failed: "The check failed. Please upload the file again.",
  notFound: "Upload not found.",
  stalled: "Still checking. Please refresh later.",
  error: "Couldn't check this upload.",
};

const EXPORT_BLOCKING_STATES = ["processing", "failed", "notFound", "stalled"];

const uploadIdFrom = (search) => {
  const value = new URLSearchParams(search).get(UPLOAD_PARAM);
  return UPLOAD_ID_PATTERN.test(value || "") ? value : undefined;
};

const blobErrorMessage = async (error) => {
  try {
    return JSON.parse(await error.response.data.text()).message;
  } catch {
    return undefined;
  }
};

const AccountReconciliation = () => {
  const { permissions } = usePermissions();
  const history = useHistory();
  const location = useLocation();
  const uploadFilter = uploadIdFrom(location.search);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [uploadCourier, setUploadCourier] = useState();
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const uploadState = uploadStatus.jobId === uploadFilter ? uploadStatus.state : undefined;
  const [uploadCheck, setUploadCheck] = useState(0);
  const [uploading, setUploading] = useState(false);
  const isMounted = useRef(true);
  const latestRequest = useRef(0);

  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    axios
      .get("/api/courier-reconciliations/service-providers")
      .then((res) => {
        if (!isMounted.current) return;
        setServiceProviders(res.data.data || []);
        setOutcomes(res.data.outcomes || []);
      })
      .catch((error) => message.error(error.response?.data?.message || error.message));
  }, []);

  const buildFilterParams = useCallback(
    () => ({
      ...(filters.serviceProvider && { serviceProvider: filters.serviceProvider }),
      ...(filters.outcome && { outcome: filters.outcome }),
      ...(filters.merchant && { merchant: filters.merchant }),
      ...(filters.trackingNo && { trackingNo: filters.trackingNo }),
      ...(uploadFilter && { adminJobId: uploadFilter }),
      ...(filters.startDate && { startDate: filters.startDate.clone().startOf("day").toISOString() }),
      ...(filters.endDate && { endDate: filters.endDate.clone().endOf("day").toISOString() }),
    }),
    [filters, uploadFilter],
  );

  const fetchRows = useCallback(async () => {
    const requestId = ++latestRequest.current;
    const isLatest = () => isMounted.current && requestId === latestRequest.current;
    setLoading(true);
    try {
      const params = { ...buildFilterParams(), page: pagination.currentPage, limit: pagination.limit };
      const res = await axios.get("/api/courier-reconciliations", { params });
      if (!isLatest()) return;
      setRows(res.data.data || []);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      if (isLatest()) message.error(error.response?.data?.message || error.message);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  const fetchRowsRef = useRef(fetchRows);
  fetchRowsRef.current = fetchRows;

  useEffect(() => {
    if (!uploadFilter) return undefined;

    let active = true;
    let timer;
    const startedAt = Date.now();
    const setUploadState = (state) => setUploadStatus({ jobId: uploadFilter, state });

    const checkUpload = async () => {
      try {
        const res = await axios.get(`/api/courier-reconciliations/uploads/${encodeURIComponent(uploadFilter)}`);
        if (!active) return;
        const state = res.data?.data?.state;
        setUploadState(state);
        if (state === "done") fetchRowsRef.current();
        if (state === "processing") checkAgainLater();
      } catch (error) {
        if (!active) return;
        const status = error.response?.status;
        if (status === 404) {
          setUploadState("notFound");
          message.error("Upload not found.");
          return;
        }
        if (status === 401 || status === 403) {
          setUploadState("error");
          message.error(error.response?.data?.message || error.message);
          return;
        }
        checkAgainLater();
      }
    };

    const checkAgainLater = () => {
      if (Date.now() - startedAt > UPLOAD_POLL_LIMIT_MS) {
        setUploadStatus((prev) =>
          prev.jobId === uploadFilter && prev.state === "done" ? prev : { jobId: uploadFilter, state: "stalled" }
        );
        return;
      }
      timer = setTimeout(checkUpload, UPLOAD_POLL_INTERVAL_MS);
    };

    checkUpload();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [uploadFilter, uploadCheck]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleFiltersChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const { data } = await axios({
        url: "/api/courier-reconciliations/template",
        method: "GET",
        params: { serviceProvider: uploadCourier },
        responseType: "blob",
      });
      saveBlob(data, `AccountReconciliation-${uploadCourier}.xlsx`);
    } catch (error) {
      message.error((await blobErrorMessage(error)) || error.message);
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const showUpload = (jobId) => {
    if (jobId) setFilters({});
    const params = new URLSearchParams(location.search);
    jobId ? params.set(UPLOAD_PARAM, jobId) : params.delete(UPLOAD_PARAM);
    history.replace({ pathname: location.pathname, search: params.toString() });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const refresh = () => {
    if (uploadFilter) setUploadCheck((count) => count + 1);
    fetchRows();
  };

  const handleUploadStart = (file) => {
    if (file.size > UPLOAD_MAX_MB * 1024 * 1024) {
      message.error(`The file is over ${UPLOAD_MAX_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    setUploading(true);
    return true;
  };

  const handleUploadError = (error) => {
    setUploading(false);
    message.error(error.response?.data?.message || error.message);
  };

  const handleUploadSuccess = (res) => {
    setUploading(false);
    const { rowCount, jobId } = res?.data?.data ?? {};
    message.info(`${rowCount} rows queued. Results will appear here when the check finishes.`);
    if (isMounted.current) showUpload(jobId);
  };

  const handleExport = () => {
    Modal.confirm({
      title: "Export account reconciliation results?",
      content: "The CSV uses the current filters. The download link will appear under System -> Jobs.",
      async onOk() {
        try {
          await axios.get("/api/courier-reconciliations/csv", { params: buildFilterParams() });
          message.info("Please check the Export progress in System -> Jobs");
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const columns = [
    { title: "Tracking No", dataIndex: "trackingNo", key: "trackingNo", width: 180 },
    { title: "Courier", dataIndex: "serviceProviderName", key: "serviceProvider", width: 150 },
    {
      title: "Merchant",
      dataIndex: "merchantEmail",
      key: "merchantEmail",
      width: 220,
      render: (email) => email || "-",
    },
    {
      title: "Order Date",
      dataIndex: "orderDate",
      key: "orderDate",
      width: 170,
      render: (date) => (date ? formatDateTime(date) : "-"),
    },
    {
      title: "Expected Account",
      dataIndex: "expectedAccount",
      key: "expectedAccount",
      width: 160,
      render: (account) => account || "-",
    },
    {
      title: "Uploaded Account",
      dataIndex: "uploadedAccount",
      key: "uploadedAccount",
      width: 160,
      render: (account) => account || "-",
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      width: 200,
      render: (outcome) => <Tag color={OUTCOME_COLORS[outcome] || "default"}>{outcome}</Tag>,
    },
    { title: "File", dataIndex: "fileName", key: "fileName", width: 200 },
    {
      title: "Uploaded At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (date) => (date ? formatDateTime(date) : "-"),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ display: "flex" }}>
      <PageHeader
        title="Account Reconciliation"
        childRoutes={[{ path: "/order/account-reconciliation", breadcrumbName: "Account Reconciliation" }]}
      />

      {permissions?.includes(PERMISSIONS.BULK_UPLOAD_ACCOUNT_RECONCILIATION) &&
        permissions?.includes(PERMISSIONS.VIEW_ACCOUNT_RECONCILIATION_PAGE) && (
        <Collapse
          defaultActiveKey={["upload"]}
          expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        >
          <Collapse.Panel header="Upload Courier File" key="upload">
            <Space direction="vertical" size="middle" style={{ display: "flex" }}>
              <Row gutter={[16, 16]} align="bottom">
                <Col xs={24} sm={12} md={8}>
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <span>Courier</span>
                    <Select
                      showSearch
                      optionFilterProp="children"
                      placeholder="Select courier"
                      style={{ width: "100%" }}
                      value={uploadCourier}
                      onChange={setUploadCourier}
                      disabled={uploading}
                    >
                      {serviceProviders.map(({ key, name }) => (
                        <Select.Option key={key} value={key}>
                          {name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Space>
                </Col>
                <Col>
                  <Button
                    icon={<DownloadOutlined />}
                    disabled={!uploadCourier}
                    loading={downloadingTemplate}
                    onClick={downloadTemplate}
                  >
                    Template
                  </Button>
                </Col>
              </Row>
              <DragUpload
                action={`${process.env.REACT_APP_SERVER_URL}/api/courier-reconciliations/upload`}
                data={{ serviceProvider: uploadCourier }}
                disabled={!uploadCourier || uploading}
                additionalDesc={`Select a courier first. Excel or CSV, up to 5000 rows and ${UPLOAD_MAX_MB} MB`}
                multiple={false}
                showSuccessMessage={false}
                beforeUpload={handleUploadStart}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
              />
            </Space>
          </Collapse.Panel>
        </Collapse>
      )}

      <Row justify="end" align="middle" gutter={8}>
        {uploadFilter && (
          <Col>
            <Tag closable color="blue" onClose={() => showUpload(null)}>
              This upload only
            </Tag>
          </Col>
        )}
        {uploadState === "processing" && (
          <Col>
            <Tag icon={<SyncOutlined spin />} color="processing">
              Checking upload
            </Tag>
          </Col>
        )}
        {uploadState === "failed" && (
          <Col>
            <Tag color="error">Check failed</Tag>
          </Col>
        )}
        {uploadState === "error" && (
          <Col>
            <Tag color="warning">{EMPTY_TEXT.error}</Tag>
          </Col>
        )}
        <Col>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={refresh}>
            Refresh
          </Button>
        </Col>
        <Col>
          <Button
            icon={<DownloadOutlined />}
            disabled={EXPORT_BLOCKING_STATES.includes(uploadState)}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      <Collapse expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}>
        <Collapse.Panel header="Filters" key="filters">
          <AccountReconciliationFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            serviceProviders={serviceProviders}
            outcomes={outcomes}
          />
        </Collapse.Panel>
      </Collapse>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{ emptyText: EMPTY_TEXT[uploadState] || "Not available" }}
      />

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>
    </Space>
  );
};

export default AccountReconciliation;
